import { useState, useEffect } from 'react';
import useImageUpload from './useImageUpload';
import useGuestImageSave from './useGuestImageSave';

const useCombinedImageUpload = () => {
  const { uploadMultipleImages, progress: uploadProgress } = useImageUpload();
  const { saveGuestImage, progress: saveProgress } = useGuestImageSave();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalImages, setTotalImages] = useState(0);
  const [completedImages, setCompletedImages] = useState(0);
  const [currentImageProgress, setCurrentImageProgress] = useState(0);

  // Track current image progress to prevent decreases
  useEffect(() => {
    if (loading && totalImages > 0) {
      // Calculate average progress from the array of upload progresses
      const uploadProgressArray = uploadProgress;
      const averageUploadProgress = uploadProgressArray.length > 0 
        ? uploadProgressArray.reduce((sum, progress) => sum + progress, 0) / uploadProgressArray.length 
        : 0;
      
      const newProgress = (averageUploadProgress * 0.9) + (saveProgress * 0.1);
      // Only update if progress increases or if we're starting a new image
      if (newProgress > currentImageProgress || currentImageProgress === 0) {
        setCurrentImageProgress(newProgress);
      }
    } else if (!loading) {
      // Reset when not loading
      setCurrentImageProgress(0);
    }
  }, [uploadProgress, saveProgress, loading, totalImages, currentImageProgress]);

  const uploadAndSave = async ({ images, event_id }) => {
    if (!images || images.length === 0) {
      setError('No image files provided');
      return null;
    }

    setLoading(true);
    setError(null);
    setTotalImages(images.length);
    setCompletedImages(0);
    setCurrentImageProgress(0);

    try {
      // Step 1 = 70% weight - Upload all images
      const imageUrls = await uploadMultipleImages(images);
      if (!imageUrls || imageUrls.length === 0) {
        setError('Image upload failed');
        setLoading(false);
        return null;
      }

      // Step 2 = 30% weight - Save all images
      const result = await saveGuestImage({
        event_id,
        image_urls: imageUrls,
      });

      if (!result) {
        setError('Saving guest images failed');
        setLoading(false);
        return null;
      }

      setLoading(false);
      return result;
    } catch (err) {
      setError(err.message || 'Upload and save failed');
      setLoading(false);
      return null;
    }
  };

  // Calculate combined progress for multiple images
  const getCombinedProgress = () => {
    if (totalImages === 0) return 0;
    
    // Average progress across all images
    // Each image has upload and save phases, so we divide by totalImages
    const averageProgress = currentImageProgress / totalImages;
    
    return Math.min(averageProgress, 100);
  };

  return {
    uploadAndSave,
    uploadMultipleImages: uploadAndSave,
   progress: currentImageProgress,
    loading,
    error,
    totalImages,
    completedImages,
    currentImageProgress,
  };
};

export default useCombinedImageUpload;
